import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, ViewEncapsulation, OnInit, AfterViewInit, Input, ViewChild, OnDestroy, TrackByFunction } from "@angular/core";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import Account from "@Models/account";
import Client from "@Models/global/client";
import { WorkbenchTask } from "@Models/config/workbench-task";
import { ConfirmationDialogService, NotificationService, NotificationSeverity } from "@Services";
import RulesDataService from "@Services/config/rules.data.service";
import { SharedModule } from "@Shared/shared.module";
import { forkJoin } from "rxjs";
import { SubSink } from "subsink";
import { MatPaginator } from "@angular/material/paginator";
import UWWorkbenchService from "@CaseSummary/underwriting/services/uw-workbench/uw-workbench.service";
import { MessageBoxComponent } from "@AccountConfig/account-settings/workbench-settings";

interface RuleItem {
    id: string;
    name: string;
}

interface CreateTaskConfig {
    name: string;
    sortOrder: number;
    ruleId?: string;
}

@Component({
    selector: 'workbenchv2-tasks-config',
    templateUrl: './workbenchv2-tasks-config.component.html',
    styleUrls: ['workbenchv2-tasks-config.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [SharedModule, MessageBoxComponent]
})

export default class WorkbenchV2TasksConfigComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() client: Client;
    @Input() account: Account;

    @ViewChild('table', {static: true}) table: MatTable<WorkbenchTask>;
    @ViewChild('paginator') paginator: MatPaginator;

    dataLoaded = false;
    dataSource = new MatTableDataSource<WorkbenchTask>();

    // Tasks
    lastUpdated: Date;
    tasksLoaded = false;
    tasks: WorkbenchTask[] = [];
    taskNamesLowerCase: string[] = [];
    addingTask = false;
    invalidTaskIds = new Set<string>(); // Track tasks with invalid names after blur
    showNameHelper = new Set<string>();
    originalTaskNames = new Map<string, string>(); // Track original names before editing
    // Rules
    rulesLoaded = false;
    noRule: RuleItem = { id: '00000000-0000-0000-0000-000000000000', name: 'None' };
    rules: RuleItem[] = [];
    

    columnsToDisplay: string[] = ['order', 'name', 'rule', 'actions'];
    
    filterValue: string = '';

    subs = new SubSink();

    constructor(
        private readonly rulesDataService: RulesDataService,
        private readonly notificationService: NotificationService,
        private readonly confirmationDialogService: ConfirmationDialogService,
        private readonly uwWorkbenchService: UWWorkbenchService
    ) {
    }

    ngOnInit() {
        this.loadData();
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    // TrackBy function to improve table performance by reducing DOM re-rendering
    trackByTaskId: TrackByFunction<WorkbenchTask> = (index: number, task: WorkbenchTask) => {
        return task.id;
    };

    private searchTimeout: any;

    // Validate if a task name is duplicate (returns true if duplicate exists)
    private isDuplicateTaskName(name: string, currentTask?: WorkbenchTask): boolean {
        const trimmedName = name?.trim().toLowerCase();
        if (!trimmedName) return false;
        
        return this.tasks.some(task => {
            // Skip comparison with the current task being edited
            if (currentTask && task.id === currentTask.id) {
                return false;
            }
            return task.name.trim().toLowerCase() === trimmedName;
        });
    }

    // Helper method to validate task name and show user feedback
    validateTaskName(name: string, currentTask?: WorkbenchTask): { isValid: boolean; errorMessage?: string } {
        if (!name?.trim()) {
            return { isValid: false, errorMessage: 'Name is required' };
        }
        
        if (this.isDuplicateTaskName(name, currentTask)) {
            return { isValid: false, errorMessage: 'Name already exists' };
        }
        
        return { isValid: true };
    }

    private loadData() {
        const observables = [
            this.loadTasks(),
            this.loadRules()
        ];

        this.subs.sink = forkJoin(observables).subscribe(() => {
            this.dataLoaded = true;
            this.refreshDataSource();
        });
    }

    private loadTasks() {
        this.tasksLoaded = false;
        return new Promise<void>((resolve, reject) => {
            this.subs.sink = this.uwWorkbenchService.getTasks(this.client.code, this.account).subscribe({
                next: (tasks) => {
                    this.tasks = tasks;
                    this.updateTaskNamesLowerCase();
                    this.tasksLoaded = true;

                    const sortedTasks = [...this.tasks].sort((a, b) => new Date(b.lastModifiedDate).getTime() - new Date(a.lastModifiedDate).getTime());
                    this.lastUpdated = new Date(sortedTasks[0]?.lastModifiedDate);

                    resolve();
                },
                error: (error) => {
                    console.error('Error loading tasks:', error);
                    this.tasks = [];
                    this.updateTaskNamesLowerCase();
                    this.tasksLoaded = true;
                    reject(error);
                }
            });
        });
    }

    private loadRules() {
        this.rulesLoaded = false;
        return new Promise<void>((resolve, reject) => {
            this.subs.sink = this.rulesDataService.load(this.account.id).subscribe({
                next: (rules) => {
                    this.rules = [this.noRule, ...rules.map(r => ({ id: r.id, name: r.name, isDisabled: false }))];
                    this.rulesLoaded = true;
                    resolve();
                },
                error: (error) => {
                    console.error('Error loading rules:', error);
                    this.rules = [this.noRule];
                    this.rulesLoaded = true;
                    reject(error);
                }
            });
        });
    }

    private updateTaskNamesLowerCase() {
        this.taskNamesLowerCase = this.tasks.map(task => task.name.trim().toLowerCase());
    }

    // Public method for testing purposes
    public updateTaskNamesLowerCaseForTesting() {
        this.updateTaskNamesLowerCase();
    }

    public refreshDataSource() {
        if (!this.tasks || !Array.isArray(this.tasks)) {
            this.tasks = [];
        }
        this.dataSource.data = [...this.tasks];
        this.updateTaskNamesLowerCase();
        
        this.dataSource.filterPredicate = this.tableFilter().bind(this);
    }

    handleAddTaskClick() {
        // Navigate to page 1 if not already there
        if (this.paginator && this.paginator.pageIndex > 0) {
            this.paginator.firstPage();
        }

        const newTask: WorkbenchTask = {
            id: `temp-${crypto.randomUUID()}`,
            accountId: this.account.id,
            name: '',
            sortOrder: 0,
            ruleId: this.noRule.id
        };
        
        this.tasks.unshift(newTask);
        this.addingTask = true;
        this.refreshDataSource();

        // Scroll to top of table after a brief delay to ensure the view has updated
        setTimeout(() => {
            const tableElement = document.querySelector('.workbench-tasks-table');
            if (tableElement) {
                tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    }

    private createTask(task: WorkbenchTask) {
        if (task.ruleId === this.noRule.id) {
            task.ruleId = undefined;
        }

        this.uwWorkbenchService.createTask(this.client.code, this.account, task).subscribe({
            next: (result) => {
                this.tasks = result;
                this.updateTaskNamesLowerCase();
                this.refreshDataSource();
            },
            error: (error) => {
                console.error('Error creating task:', error);
                this.notificationService.showNotification({ message: 'Error creating task', severity: NotificationSeverity.Error });
            }
        });
    }

    handleDeleteTask(task: WorkbenchTask) {
        if (task.id.startsWith('temp-')) {
            this.tasks = this.tasks.filter(t => t.id !== task.id);
            this.refreshDataSource();
            return;
        }

        this.confirmationDialogService.open({
            title: 'Delete Task',
            message: `Are you sure you want to delete the task "${task.name}"?`,
            showCancel: true,
            onOk: () => {
                this.deleteTask(task);
            }
        });
    }

    private deleteTask(task: WorkbenchTask) {
        this.uwWorkbenchService.deleteTask(this.client.code, this.account, task.id).subscribe({
            next: (result) => {
                this.tasks = result;
                this.updateTaskNamesLowerCase();
                this.refreshDataSource();
                this.notificationService.showNotification({message: 'Task deleted successfully', severity: NotificationSeverity.Success });
            },
            error: (error) => {
                console.error('Error deleting task:', error);
                this.notificationService.showNotification({ message: 'Error deleting task', severity: NotificationSeverity.Error });
            }
        });
    }

    handleSortChange(event: CdkDragDrop<WorkbenchTask[]>) {
        // Account for pagination when calculating actual indices
        const pageSize = this.paginator?.pageSize || this.tasks.length;
        const pageIndex = this.paginator?.pageIndex || 0;
        const offset = pageIndex * pageSize;
        
        const actualPreviousIndex = offset + event.previousIndex;
        const actualCurrentIndex = offset + event.currentIndex;
        
        moveItemInArray(this.tasks, actualPreviousIndex, actualCurrentIndex);
        this.reorderTasks();
        this.refreshDataSource();
    }

    handleMoveUp(task: WorkbenchTask) {
        const currentIndex = this.tasks.findIndex(t => t.id === task.id);
        if (currentIndex > 0) {
            [this.tasks[currentIndex], this.tasks[currentIndex - 1]] = [this.tasks[currentIndex - 1], this.tasks[currentIndex]];
            this.reorderTasks();
            this.refreshDataSource();
        }
    }

    handleMoveDown(task: WorkbenchTask) {
        const currentIndex = this.tasks.findIndex(t => t.id === task.id);
        if (currentIndex < this.tasks.length - 1) {
            [this.tasks[currentIndex], this.tasks[currentIndex + 1]] = [this.tasks[currentIndex + 1], this.tasks[currentIndex]];
            this.reorderTasks();
            this.refreshDataSource();
        }
    }

    handleMoveToPosition(task: WorkbenchTask, newPosition: number) {
        const targetPosition = Math.max(1, Math.min(newPosition, this.tasks.length)) - 1;
        const currentIndex = this.tasks.findIndex(t => t.id === task.id);
        
        if (currentIndex !== -1 && currentIndex !== targetPosition) {
            const movedTask = this.tasks.splice(currentIndex, 1)[0];
            this.tasks.splice(targetPosition, 0, movedTask);
            this.reorderTasks();
            this.refreshDataSource();
        }
    }

    private reorderTasks() {
        this.tasks.forEach((task, index) => {
            task.sortOrder = index+1;
            
            if (task.ruleId === this.noRule.id) {
                task.ruleId = undefined;
            }
        });

        this.uwWorkbenchService.updateTasks(this.client.code, this.account, this.tasks).subscribe({
            next: (result) => {
                this.tasks = result;
                this.updateTaskNamesLowerCase();
                this.refreshDataSource();
                this.notificationService.showNotification({ message: 'Tasks reordered successfully', severity: NotificationSeverity.Success });
            },
            error: (error) => {
                console.error('Error reordering tasks:', error);
                this.notificationService.showNotification({ message: 'Error reordering tasks', severity: NotificationSeverity.Error });
            }
        });
    }

    searchTasks() {        
        this.dataSource.filter = 'filter';
    }

    tableFilter(): (task: WorkbenchTask, filter: string) => boolean {
        return function (task, filter): boolean {
            if (this.filterValue && this.filterValue.trim() !== '') {     
                return task.name.toLowerCase().includes(this.filterValue.toLowerCase()) ||
                   this.rules.find(r => r.id === task.ruleId)?.name.toLowerCase().includes(this.filterValue.toLowerCase()) ||
                   task.id.toLowerCase().includes('temp-');                       
            }
            
            return true;
        }
    }

    // Auto-save functionality for inline editing
    onNameChange(task: WorkbenchTask, newName: string, item?: any) {
        const validation = this.validateTaskName(newName, task);
        if (!validation.isValid) {
            this.invalidTaskIds.add(task.id);
            item?.control?.setErrors({'required': true});
            this.notificationService.showNotification({ message: validation.errorMessage, severity: NotificationSeverity.Error });
            return;
        }

        // Remove from invalid set if validation passes
        this.invalidTaskIds.delete(task.id);

        const originalName = this.originalTaskNames.get(task.id) || '';
        
        if (task.id.startsWith('temp-')) {      
            const updatedTask = { ...task, name: newName.trim(), id: null };  
            this.createTask(updatedTask);
        } else if (newName.trim() !== originalName && newName.trim() !== '') {
            const updatedTask = { ...task, name: newName.trim() };
            this.updateTaskInline(updatedTask);
        }

        // Clean up the stored original name
        this.originalTaskNames.delete(task.id);
    }

    // Check if task name should be displayed as invalid
    isTaskNameInvalid(task: WorkbenchTask): boolean {
        return this.invalidTaskIds.has(task.id);
    }

    // Clamp position input value to valid range
    clampPositionValue(event: any) {
        const input = event.target;
        const value = +input.value;
        const maxValue = this.tasks?.length || 1;
        input.value = Math.max(1, Math.min(value, maxValue));
    }

    onRuleChange(task: WorkbenchTask, ruleId: string) {
        const updatedTask = { ...task, ruleId: ruleId === this.noRule.id ? undefined : ruleId };
        this.updateTaskInline(updatedTask);
    }

    private updateTaskInline(task: WorkbenchTask) {        
        if (task.ruleId === this.noRule.id) {
            task.ruleId = undefined;
        }

        this.uwWorkbenchService.updateTasks(this.client.code, this.account, [task]).subscribe({
            next: () => {
                this.updateTaskInList(task);
            },
            error: (error) => {
                console.error('Error updating task:', error);
                this.notificationService.showNotification({message: 'Error updating task', severity: NotificationSeverity.Error });
            }
        });
    }

    private updateTaskInList(task: WorkbenchTask) {
        const taskIndex = this.tasks.findIndex(t => t.id === task.id);
        if (taskIndex !== -1) {
            this.tasks[taskIndex] = task;
            this.refreshDataSource();
            this.notificationService.showNotification({message: 'Task updated successfully', severity: NotificationSeverity.Success });
        }
    }
}